import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Eye, EyeOff, Ticket } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { uploadImage, resolveImageUrl } from '../../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '', description: '', location: '', event_date: '', time: '18:00', end_time: '',
  capacity: 0, price: 0, image_url: '', add_ons: [], addon_selection: 'multiple', terms: [], is_hidden: false,
};

const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/events`, { headers: { Authorization: `Bearer ${token}` } });
      setEvents(res.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.event_date) { toast.error('Please set an event date'); return; }
    try {
      const token = localStorage.getItem('admin_token');
      const payload = { ...formData, capacity: Number(formData.capacity) || 0, price: Number(formData.price) || 0 };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Event created');
      }
      fetchEvents();
      handleClose();
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      name: event.name, description: event.description, location: event.location || '',
      event_date: event.event_date || '', time: event.time || '18:00', end_time: event.end_time || '',
      capacity: event.capacity || 0, price: event.price ?? 0, image_url: event.image_url || '',
      add_ons: event.add_ons || [], addon_selection: event.addon_selection || 'multiple', terms: event.terms || [], is_hidden: event.is_hidden || false,
    });
    setIsDialogOpen(true);
  };

  const handleDuplicate = (event) => {
    setEditingEvent(null);
    setFormData({
      name: event.name, description: event.description, location: event.location || '',
      event_date: '', time: event.time || '18:00', end_time: event.end_time || '',
      capacity: event.capacity || 0, price: event.price ?? 0, image_url: event.image_url || '',
      add_ons: (event.add_ons || []).map((a) => ({ ...a })), addon_selection: event.addon_selection || 'multiple', terms: (event.terms || []).map((t) => ({ ...t })), is_hidden: false,
    });
    setIsDialogOpen(true);
    toast.info('Set a new date, then save to create the duplicate event');
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setEditingEvent(null);
    setFormData(emptyForm);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/events/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleToggleVisibility = async (event) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/events/${event.id}/visibility`, { is_hidden: !event.is_hidden }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(event.is_hidden ? 'Event is now visible' : 'Event hidden from visitors');
      fetchEvents();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const handleImage = async (file) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setFormData((p) => ({ ...p, image_url: url }));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to upload image');
    }
  };

  const addAddon = () => setFormData((p) => ({ ...p, add_ons: [...p.add_ons, { id: `new-${Date.now()}`, name: '', price: 0 }] }));
  const updateAddon = (i, field, value) => setFormData((p) => {
    const arr = [...p.add_ons];
    arr[i] = { ...arr[i], [field]: field === 'price' ? value : value };
    return { ...p, add_ons: arr };
  });
  const removeAddon = (i) => setFormData((p) => ({ ...p, add_ons: p.add_ons.filter((_, idx) => idx !== i) }));

  const addTerm = () => setFormData((p) => ({ ...p, terms: [...(p.terms || []), { id: `new-${Date.now()}`, text: '', required: true }] }));
  const updateTerm = (i, field, value) => setFormData((p) => {
    const arr = [...(p.terms || [])];
    arr[i] = { ...arr[i], [field]: value };
    return { ...p, terms: arr };
  });
  const removeTerm = (i) => setFormData((p) => ({ ...p, terms: (p.terms || []).filter((_, idx) => idx !== i) }));

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : null;

  const visibleEvents = events.filter((e) => !e.is_hidden);
  const hiddenEvents = events.filter((e) => e.is_hidden);

  const renderCard = (event) => (
    <div key={event.id} className="bg-white border border-zinc-200 overflow-hidden hover:border-black transition-colors" data-testid={`event-admin-card-${event.id}`}>
      <div className="relative aspect-[3/4] bg-cover bg-center bg-zinc-100" style={{ backgroundImage: `url('${resolveImageUrl(event.image_url)}')` }}>
        {event.is_hidden && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wide bg-zinc-900/90 text-white px-2 py-1 rounded-full flex items-center gap-1">
            <EyeOff size={12} /> Hidden
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>{event.name}</h3>
        <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{event.description}</p>
        <div className="space-y-1 text-sm mb-4">
          <p><strong>Date:</strong> {formatDate(event.event_date)}</p>
          <p><strong>Time:</strong> {event.time}{event.end_time ? ` – ${event.end_time}` : ''}</p>
          {event.location && <p><strong>Location:</strong> {event.location}</p>}
          <p><strong>Price:</strong> ₱{Number(event.price || 0).toLocaleString()}</p>
          <p data-testid={`event-slots-${event.id}`}>
            <strong>Tickets:</strong>{' '}
            {event.sold_out ? (
              <span className="text-red-600 font-bold">SOLD OUT</span>
            ) : event.capacity ? (
              <span className="text-green-700 font-bold">{event.slots_remaining} / {event.capacity}</span>
            ) : (
              <span className="text-zinc-500">Unlimited</span>
            )}{' '}
            <span className="text-zinc-400">({event.confirmed_count} sold)</span>
          </p>
          {event.add_ons?.length > 0 && (
            <p><strong>Add-ons:</strong> {event.add_ons.map((a) => a.name).join(', ')}</p>
          )}
        </div>
        <div className="space-y-2">
          <button onClick={() => handleToggleVisibility(event)} className={`w-full rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${event.is_hidden ? 'bg-green-100 hover:bg-green-200 text-green-700' : 'bg-amber-100 hover:bg-amber-200 text-amber-700'}`} data-testid={`toggle-visibility-event-${event.id}`}>
            {event.is_hidden ? <><Eye size={16} /> SHOW TO VISITORS</> : <><EyeOff size={16} /> HIDE FROM VISITORS</>}
          </button>
          <button onClick={() => handleDuplicate(event)} className="w-full bg-zinc-900 hover:bg-black text-white rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2" data-testid={`duplicate-event-${event.id}`}>
            <Ticket size={16} /> DUPLICATE (NEW DATE)
          </button>
          <div className="flex gap-2">
            <button onClick={() => handleEdit(event)} className="flex-1 bg-zinc-100 hover:bg-zinc-200 text-black rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2" data-testid={`edit-event-${event.id}`}>
              <Edit size={16} /> EDIT
            </button>
            <button onClick={() => handleDelete(event.id)} className="flex-1 bg-red-100 hover:bg-red-200 text-red-600 rounded px-4 py-2 text-sm font-medium transition-colors flex items-center justify-center gap-2" data-testid={`delete-event-${event.id}`}>
              <Trash2 size={16} /> DELETE
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-8" data-testid="events-management">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-8">
        <h2 className="text-3xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Events Management</h2>
        <button onClick={() => { setFormData(emptyForm); setEditingEvent(null); setIsDialogOpen(true); }} className="bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center gap-2" data-testid="add-event-btn">
          <Plus size={20} /> ADD EVENT
        </button>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="visible-events-header">
        <Eye size={18} className="text-zinc-500" />
        <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Visible to Visitors</h3>
        <span className="text-sm text-zinc-400">({visibleEvents.length})</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {visibleEvents.length === 0 ? (
          <p className="text-zinc-400 text-sm" data-testid="no-visible-events">No visible events.</p>
        ) : visibleEvents.map(renderCard)}
      </div>

      {hiddenEvents.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-4 mt-12 pt-8 border-t border-zinc-200" data-testid="hidden-events-header">
            <EyeOff size={18} className="text-zinc-500" />
            <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--heading-font)' }}>Hidden from Visitors</h3>
            <span className="text-sm text-zinc-400">({hiddenEvents.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
            {hiddenEvents.map(renderCard)}
          </div>
        </>
      )}

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="ev-name">Event Name *</Label>
              <Input id="ev-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="event-form-name" />
            </div>
            <div>
              <Label htmlFor="ev-desc">Description *</Label>
              <textarea id="ev-desc" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows={4} className="w-full border border-zinc-300 rounded px-3 py-2 text-sm" data-testid="event-form-description" />
              <p className="text-xs text-zinc-500 mt-1">Line breaks and spacing you enter here are shown exactly as-is to visitors (use blank lines to separate paragraphs). They can expand the full text with "See more" on the Events page.</p>
            </div>
            <div>
              <Label htmlFor="ev-location">Location</Label>
              <Input id="ev-location" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} data-testid="event-form-location" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ev-date">Event Date *</Label>
                <Input id="ev-date" type="date" value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} required data-testid="event-form-date" />
              </div>
              <div>
                <Label htmlFor="ev-time">Start Time *</Label>
                <Input id="ev-time" type="time" value={formData.time} onChange={(e) => setFormData({ ...formData, time: e.target.value })} required data-testid="event-form-time" />
              </div>
              <div>
                <Label htmlFor="ev-end">End Time</Label>
                <Input id="ev-end" type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} data-testid="event-form-end-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ev-capacity">Capacity (0 = unlimited)</Label>
                <Input id="ev-capacity" type="number" min="0" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: e.target.value })} data-testid="event-form-capacity" />
              </div>
              <div>
                <Label htmlFor="ev-price">Ticket Price (₱) *</Label>
                <Input id="ev-price" type="number" min="0" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required data-testid="event-form-price" />
              </div>
            </div>

            {/* Add-ons editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Add-ons (optional checkboxes for buyers)</Label>
                <button type="button" onClick={addAddon} className="text-sm font-medium text-black underline" data-testid="event-add-addon-btn">+ Add option</button>
              </div>
              <div className="flex items-center gap-4 mb-3 text-sm" data-testid="event-addon-selection-mode">
                <span className="text-zinc-500">Selection mode:</span>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="addon_selection" checked={formData.addon_selection === 'multiple'} onChange={() => setFormData({ ...formData, addon_selection: 'multiple' })} className="accent-black" data-testid="addon-mode-multiple" />
                  Multiple (checkboxes)
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" name="addon_selection" checked={formData.addon_selection === 'single'} onChange={() => setFormData({ ...formData, addon_selection: 'single' })} className="accent-black" data-testid="addon-mode-single" />
                  Choose one (radio buttons)
                </label>
              </div>
              <div className="space-y-2">
                {formData.add_ons.length === 0 && <p className="text-xs text-zinc-400">No add-ons. Buyers will only pay the ticket price.</p>}
                {formData.add_ons.map((a, i) => (
                  <div key={a.id || i} className="flex items-center gap-2" data-testid={`event-form-addon-${i}`}>
                    <Input placeholder="Add-on name (e.g. Event T-shirt)" value={a.name} onChange={(e) => updateAddon(i, 'name', e.target.value)} className="flex-1" data-testid={`event-form-addon-name-${i}`} />
                    <Input type="number" min="0" placeholder="₱0" value={a.price} onChange={(e) => updateAddon(i, 'price', Number(e.target.value))} className="w-28" data-testid={`event-form-addon-price-${i}`} />
                    <button type="button" onClick={() => removeAddon(i)} className="p-2 text-red-600 hover:bg-red-100 rounded" data-testid={`event-form-addon-remove-${i}`}><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-zinc-200 rounded p-3" data-testid="event-terms-editor">
              <div className="flex items-center justify-between mb-2">
                <Label>Terms &amp; Conditions</Label>
                <button type="button" onClick={addTerm} className="text-sm font-medium text-black underline" data-testid="event-add-term-btn">+ Add term</button>
              </div>
              <p className="text-xs text-zinc-500 mb-3">Each term becomes a checkbox in the ticket dialog. Required terms must be ticked to pay; optional ones don't block. What each visitor checked is saved to the booking &amp; ticket.</p>
              <div className="space-y-2">
                {(!formData.terms || formData.terms.length === 0) && <p className="text-xs text-zinc-400">No terms. Buyers can proceed to payment directly.</p>}
                {(formData.terms || []).map((t, i) => (
                  <div key={t.id || i} className="flex items-start gap-2" data-testid={`event-form-term-${i}`}>
                    <div className="flex-1">
                      <textarea
                        placeholder="e.g. All ticket sales are final and non-refundable."
                        value={t.text}
                        onChange={(e) => updateTerm(i, 'text', e.target.value)}
                        rows={2}
                        className="w-full border border-zinc-300 rounded px-3 py-2 text-sm"
                        data-testid={`event-form-term-input-${i}`}
                      />
                      <label className="inline-flex items-center gap-2 mt-1 text-xs cursor-pointer">
                        <input
                          type="checkbox"
                          checked={t.required !== false}
                          onChange={(e) => updateTerm(i, 'required', e.target.checked)}
                          className="w-4 h-4 accent-black"
                          data-testid={`event-form-term-required-${i}`}
                        />
                        Required (must be ticked to pay)
                      </label>
                    </div>
                    <button type="button" onClick={() => removeTerm(i)} className="p-2 text-red-600 hover:bg-red-100 rounded" data-testid={`event-form-term-remove-${i}`}><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Event Image</Label>
              <div className="flex items-center gap-4 mt-1">
                {formData.image_url && (
                  <img src={resolveImageUrl(formData.image_url)} alt="preview" className="h-20 w-20 object-cover rounded border" />
                )}
                <input type="file" accept="image/*" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="event-form-image" />
              </div>
            </div>

            <div className="pt-2">
              <button type="submit" className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95" data-testid="event-form-submit">
                {editingEvent ? 'UPDATE EVENT' : 'CREATE EVENT'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventsManagement;
